// ABOUTME: Retarder configuration form — 3 NumericalInput fields (Minimum, Maximum, Change increment).
// ABOUTME: Uses react-hook-form; each label has an ⓘ tooltip; read-only when scope is below country.
import {Icon, TextMedium} from '@hm/fundamental'
import {CountryConfigsTooltip} from './CountryConfigsTooltip'
import {mdiInformationOutline} from '@mdi/js'
import React, {useEffect, useMemo} from 'react'
import {useForm, useWatch} from 'react-hook-form'

import {useTranslation} from '../../../../hooks/useTranslation'
import {RetarderEntityOptions} from '../types'
import {RetarderConfig, RetarderFormValues} from '../types/retarder'
import {uomRefsToOptions} from '../utils/entityCatalogOptions'
import {FilterDropdown} from './FilterDropdown'
import {NumericalInput} from './NumericalInput'

export interface RetarderFormProps {
  config: RetarderConfig
  isReadOnly: boolean
  onDirtyChange: (dirty: boolean) => void
  onValidChange: (valid: boolean) => void
  onValuesChange?: (values: RetarderFormValues) => void
  /** Backend-provided UoM options. When present, a selectable UoM dropdown is shown. */
  options?: RetarderEntityOptions
}

type NumericField = keyof Pick<RetarderFormValues, 'min' | 'max' | 'step'>

const FIELDS: {key: NumericField; labelKey: string; tooltipKey: string}[] = [
  {
    key: 'min',
    labelKey: 'countryConfigurations.retarder.minimum',
    tooltipKey: 'countryConfigurations.retarder.minimumTooltip'
  },
  {
    key: 'max',
    labelKey: 'countryConfigurations.retarder.maximum',
    tooltipKey: 'countryConfigurations.retarder.maximumTooltip'
  },
  {
    key: 'step',
    labelKey: 'countryConfigurations.retarder.changeIncrement',
    tooltipKey: 'countryConfigurations.retarder.changeIncrementTooltip'
  }
]

const toFormValues = (cfg: RetarderConfig): RetarderFormValues => ({
  min: cfg.min,
  max: cfg.max,
  step: cfg.step,
  uom: cfg.uom,
  uomId: cfg.uomId
})

export const RetarderForm: React.FC<RetarderFormProps> = ({
  config,
  isReadOnly,
  onDirtyChange,
  onValidChange,
  onValuesChange,
  options
}) => {
  const {t} = useTranslation()
  const formDefaultValues = useMemo(() => toFormValues(config), [config])
  const {control, setValue, reset, formState: {isDirty}} = useForm<RetarderFormValues>({
    defaultValues: formDefaultValues,
  })

  useEffect(() => {
    reset(formDefaultValues)
  }, [formDefaultValues, reset])

  const min = useWatch({control, name: 'min'})
  const max = useWatch({control, name: 'max'})
  const step = useWatch({control, name: 'step'})
  const uom = useWatch({control, name: 'uom'})
  const uomId = useWatch({control, name: 'uomId'})

  const values: RetarderFormValues = useMemo(() => ({min, max, step, uom, uomId}), [min, max, step, uom, uomId])

  // Show a selectable UoM dropdown only when the backend provides options; otherwise keep the
  // existing behaviour (UoM fixed from config, shown as the numeric input suffix).
  const uomOptions = useMemo(() => uomRefsToOptions(options?.availableUnitsOfMeasure), [options])
  const showUomDropdown = uomOptions.length > 0

  const errors = useMemo<Partial<Record<NumericField, string>>>(() => {
    const errs: Partial<Record<NumericField, string>> = {}
    if (min < 0) {
      errs.min = t('countryConfigurations.retarder.errorNonNegative')
    } else if (min > max) {
      errs.min = t('countryConfigurations.retarder.errorMinExceedsMax')
    }
    if (max < 0) {
      errs.max = t('countryConfigurations.retarder.errorNonNegative')
    } else if (max < min) {
      errs.max = t('countryConfigurations.retarder.errorMaxBelowMin')
    }
    if (step <= 0) {
      errs.step = t('countryConfigurations.retarder.errorStepPositive')
    }
    return errs
  }, [min, max, step, t])

  // Saving always sends the UoM as overridden, and the backend rejects an empty one.
  const uomError = uomId
    ? undefined
    : t(showUomDropdown
      ? 'countryConfigurations.retarder.errorUomRequired'
      : 'countryConfigurations.retarder.errorNoUomAvailable')

  const isValid = Object.keys(errors).length === 0 && !uomError

  useEffect(() => {
    onDirtyChange(isDirty)
  }, [isDirty, onDirtyChange])

  useEffect(() => {
    onValidChange(isValid)
  }, [isValid, onValidChange])

  useEffect(() => {
    if (isDirty) {
      onValuesChange?.(values)
    }
  }, [values, isDirty, onValuesChange])

  const handleChange = (field: NumericField) => (val: number) => {
    setValue(field, val, {shouldDirty: true})
  }

  return (
    <div className="flex flex-col gap-6 p-6 w-full" data-cy="retarder-form">
      {showUomDropdown && (
        <div className="flex gap-14 items-start w-full">
          <div className="flex flex-col justify-center shrink-0 h-10 min-w-48 max-w-72">
            <TextMedium className="text-foreground line-clamp-1">
              {t('countryConfigurations.retarder.uom')}
            </TextMedium>
          </div>
          <div className="w-[228px] shrink-0">
            <FilterDropdown
              value={values.uomId ?? ''}
              onChange={(v) => {
                const opt = uomOptions.find((o) => o.value === v)
                setValue('uomId', v, {shouldDirty: true})
                setValue('uom', opt?.label ?? '', {shouldDirty: true})
              }}
              options={uomOptions}
              label={t('countryConfigurations.retarder.uom')}
              size="medium"
              disabled={isReadOnly}
              data-cy="retarder-uom"
            />
            {uomError && <p role="alert" className="text-xs leading-4 text-negative px-3">{uomError}</p>}
          </div>
        </div>
      )}
      {!showUomDropdown && uomError && (
        <p role="alert" className="text-sm text-negative" data-cy="retarder-uom-error">{uomError}</p>
      )}
      {FIELDS.map((field) => {
        const isFieldDirty = values[field.key] !== config[field.key]
        return (
          <div key={field.key} className="flex gap-14 items-start w-full">
            {/* Label area */}
            <div className="flex flex-col justify-center shrink-0 h-10 min-w-48 max-w-72">
              <div className="flex gap-0.5 items-center whitespace-nowrap">
                <TextMedium className="text-foreground line-clamp-1">{t(field.labelKey)}</TextMedium>
                <CountryConfigsTooltip content={t(field.tooltipKey)} delayMs={300}>
                  <span className="text-foreground flex items-center cursor-default">
                    <Icon path={mdiInformationOutline} size="regular" />
                  </span>
                </CountryConfigsTooltip>
              </div>
              {isFieldDirty && !isReadOnly && (
                <span className="text-xs leading-4 text-warning">
                  {t('countryConfigurations.retarder.valueChangedUnsaved')}
                </span>
              )}
            </div>

            {/* Input */}
            <div className="w-[228px] shrink-0">
            <NumericalInput
              value={values[field.key]}
              onChange={handleChange(field.key)}
              min={0}
              step={field.key !== 'step' ? values.step : 1}
              suffix={values.uom}
              disabled={isReadOnly}
              error={errors[field.key]}
              data-cy={`retarder-field-${field.key}`}
            />
            </div>
          </div>
        )
      })}
    </div>
  )
}
